import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { Icons } from '../Icons';
import { ToastIcons } from './ToastIcons';

const toastVariants = cva(
  'relative flex w-full max-w-[430px] items-start gap-3 rounded-lg border p-4',
  {
    variants: {
      variant: {
        info: 'border-[var(--info-border)] bg-[var(--info-bg)]',
        success: 'border-[var(--success-border)] bg-[var(--success-bg)]',
        warning: 'border-[var(--warning-border)] bg-[var(--warning-bg)]',
        error: 'border-[var(--error-border)] bg-[var(--error-bg)]',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

const titleVariants = cva('text-sm font-semibold leading-tight', {
  variants: {
    variant: {
      info: 'text-[var(--info-heading)]',
      success: 'text-[var(--success-heading)]',
      warning: 'text-[var(--warning-heading)]',
      error: 'text-[var(--error-heading)]',
    },
  },
});

const descriptionVariants = cva('text-sm font-normal leading-snug', {
  variants: {
    variant: {
      info: 'text-[var(--info-subtext)]',
      success: 'text-[var(--success-subtext)]',
      warning: 'text-[var(--warning-subtext)]',
      error: 'text-[var(--error-subtext)]',
    },
  },
});

const iconMap = {
  info: ToastIcons.Info,
  success: ToastIcons.Success,
  warning: ToastIcons.Warning,
  error: ToastIcons.Error,
} as const;

export type ToastVariant = keyof typeof iconMap;

export interface ToastProps extends VariantProps<typeof toastVariants> {
  title: string;
  description?: string;
  onClose?: () => void;
  className?: string;
  showClose?: boolean;
}

const Toast = ({
  variant = 'info',
  title,
  description,
  onClose,
  className,
  showClose = true,
}: ToastProps) => {
  const Icon = iconMap[variant ?? 'info'];

  return (
    <div
      className={cn(toastVariants({ variant }), className)}
      role="alert"
    >
      <Icon className="mt-0.5 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-1 pr-5">
        <p className={titleVariants({ variant })}>{title}</p>
        {description ? (
          <p className={descriptionVariants({ variant })}>{description}</p>
        ) : null}
      </div>
      {showClose ? (
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-3 top-3 text-[#737373] transition-colors hover:text-[#525252]"
        >
          <Icons.Close className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
};

export { Toast, toastVariants };
